/**
 * ResetPasswordForm - Reset Password Form Component
 *
 * Pure presentation component for password reset functionality.
 * Handles new password input, confirmation, validation, and submission.
 *
 * Features:
 * - New password field with show/hide toggle
 * - Confirm password field
 * - Password strength indicator (5-bar visual)
 * - Password match validation
 * - Loading state
 * - Error/success message display
 * - Link back to login
 *
 * Used in: ResetPasswordTemplate
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../atoms/button';
import { Input } from '../atoms/input';
import { Label } from '../atoms/label';
import { Eye, EyeOff } from 'lucide-react';

export interface ResetPasswordFormProps {
    /** Callback when form is submitted with new password */
    onSubmit: (password: string) => Promise<void>;

    /** Loading state during submission */
    isLoading?: boolean;

    /** Platform flag for styling (web vs plugin) */
    isWeb?: boolean;

    /** Initial error message to display */
    initialError?: string;

    /** Callback to navigate back to login */
    onBackToLogin?: () => void;

    /** Reset token (for display/validation) */
    token?: string;
}

interface FormData {
    password: string;
    confirmPassword: string;
}

interface FormErrors {
    password?: string;
    confirmPassword?: string;
    general?: string;
}

interface PasswordStrength {
    score: number;
    isValid: boolean;
    className: string;
}

/**
 * ResetPasswordForm Component
 *
 * Handles the password reset form with strength validation.
 */
export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
    onSubmit,
    isLoading = false,
    isWeb = true,
    initialError,
    onBackToLogin,
    token,
}) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<FormData>({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
        score: 0,
        isValid: false,
        className: 'bg-gray-200',
    });

    // Set initial error if provided
    useEffect(() => {
        if (initialError) {
            setErrors({ general: initialError });
        }
    }, [initialError]);

    // Password strength calculation (same as RegisterForm)
    const calculatePasswordStrength = useCallback((password: string) => {
        let strength = 0;

        if (password.match(/[a-z]/)) strength += 1; // lowercase
        if (password.match(/[A-Z]/)) strength += 1; // uppercase
        if (password.match(/[0-9]/)) strength += 1; // numbers
        if (password.match(/[^a-zA-Z0-9]/)) strength += 1; // special chars
        if (password.length > 7) strength += 1; // length

        const strengthMap: Record<number, PasswordStrength> = {
            0: { score: 0, isValid: false, className: 'bg-gray-200' },
            1: { score: 1, isValid: false, className: 'bg-red-500' },
            2: { score: 2, isValid: false, className: 'bg-orange-500' },
            3: { score: 3, isValid: false, className: 'bg-yellow-500' },
            4: { score: 4, isValid: false, className: 'bg-lime-500' },
            5: { score: 5, isValid: true, className: 'bg-green-500' },
        };

        const currentStrength = strengthMap[strength] || strengthMap[5];
        setPasswordStrength(currentStrength);
    }, []);

    // Validate form
    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};

        // Password validation
        if (!formData.password) {
            newErrors.password = t('auth.passwordRequired');
        } else if (!passwordStrength.isValid) {
            newErrors.password = t('auth.passwordHint');
        }

        // Confirm password validation
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = t('auth.confirmPasswordRequired');
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = t('auth.passwordsNoMatch');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, passwordStrength.isValid, t]);

    // Handle password input change
    const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value;
        setFormData(prev => ({ ...prev, password: newPassword }));
        calculatePasswordStrength(newPassword);

        // Clear field-specific error
        if (errors.password) {
            setErrors(prev => ({ ...prev, password: undefined }));
        }
        // Clear general error
        if (errors.general) {
            setErrors(prev => ({ ...prev, general: undefined }));
        }
        // Clear success message
        if (successMessage) {
            setSuccessMessage('');
        }
    }, [errors.password, errors.general, successMessage, calculatePasswordStrength]);

    // Handle confirm password input change
    const handleConfirmPasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newConfirmPassword = e.target.value;
        setFormData(prev => ({ ...prev, confirmPassword: newConfirmPassword }));

        // Clear field-specific error
        if (errors.confirmPassword) {
            setErrors(prev => ({ ...prev, confirmPassword: undefined }));
        }
        // Clear general error
        if (errors.general) {
            setErrors(prev => ({ ...prev, general: undefined }));
        }
        // Clear success message
        if (successMessage) {
            setSuccessMessage('');
        }
    }, [errors.confirmPassword, errors.general, successMessage]);

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm() || isLoading) {
            return;
        }

        try {
            await onSubmit(formData.password);
            // Show success message
            setSuccessMessage(t('auth.resetSuccess'));
            // Clear form
            setFormData({ password: '', confirmPassword: '' });
            setErrors({});
        } catch (error: any) {
            setErrors({ general: error.message || t('auth.resetFailed') });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="w-full space-y-6">
            {/* New Password Field */}
            <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                    {t('auth.newPassword')}
                </Label>
                <div className="relative">
                    <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('auth.enterNewPassword')}
                        value={formData.password}
                        onChange={handlePasswordChange}
                        className={`w-full pe-10 ${errors.password ? 'border-red-500' : ''}`}
                        disabled={isLoading}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                    >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
                {errors.password && (
                    <p className="text-xs text-red-600">{errors.password}</p>
                )}

                {/* Password Strength Indicator */}
                {formData.password && (
                    <div className="space-y-1">
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((bar) => (
                                <div
                                    key={bar}
                                    className={`h-1.5 flex-1 rounded-full transition-colors ${bar <= passwordStrength.score
                                            ? passwordStrength.className
                                            : 'bg-gray-200'
                                        }`}
                                />
                            ))}
                        </div>
                        <p className="text-xs text-gray-600">
                            {t('auth.passwordStrength')} {
                                passwordStrength.score === 0 ? t('auth.strengthNone') :
                                    passwordStrength.score === 1 ? t('auth.strengthWeak') :
                                        passwordStrength.score === 2 ? t('auth.strengthWeak') :
                                            passwordStrength.score === 3 ? t('auth.strengthFair') :
                                                passwordStrength.score === 4 ? t('auth.strengthGood') :
                                                    t('auth.strengthStrong')
                            }
                        </p>
                    </div>
                )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    {t('auth.confirmNewPassword')}
                </Label>
                <div className="relative">
                    <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={t('auth.confirmNewPasswordPlaceholder')}
                        value={formData.confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        className={`w-full pe-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                        disabled={isLoading}
                        autoComplete="new-password"
                    />
                    <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        tabIndex={-1}
                    >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
                {errors.confirmPassword && (
                    <p className="text-xs text-red-600">{errors.confirmPassword}</p>
                )}
            </div>

            {/* Submit Button */}
            <Button
                type="submit"
                variant="primary"
                size={isWeb ? 'xl' : 'lg'}
                className="w-full"
                disabled={isLoading}
            >
                {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('auth.resettingPassword')}
                    </span>
                ) : (
                    t('auth.resetPassword')
                )}
            </Button>

            {/* Back to Login Link */}
            {onBackToLogin && (
                <div className="text-center pt-2">
                    <button
                        type="button"
                        onClick={onBackToLogin}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                    >
                        {t('auth.backToLogin')}
                    </button>
                </div>
            )}
        </form>
    );
};
